import { apiClient } from '../apiClient';

/**
 * Helper to build RPC inventory base URL
 * CRITICAL: Validates company before building URL to prevent 404s
 */
const inventoryRpcBase = (company: string): string => {
  // CRITICAL FIX: Validate company parameter
  if (!company || company === 'undefined' || company.trim() === '') {
    const error = new Error('Company slug is required but not available. Please ensure you are on a valid company page.');
    console.error('[inventory] inventoryRpcBase failed:', error.message, {
      providedCompany: company,
    });
    throw error; // Throw error to prevent malformed request
  }
  return `/api/${company}/rpc/inventory`;
};

/**
 * Inventory item type
 */
export interface InventoryItem {
  _id: string;
  company: string;
  sku: string;
  name: string;
  price: number;
  stock: number;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

/**
 * List inventory items
 */
export async function listItems(
  company: string,
  filters?: {
    q?: string;
    tag?: string | string[];
  }
): Promise<InventoryItem[]> {
  const { data } = await apiClient.post(
    `${inventoryRpcBase(company)}/list-items`,
    filters || {},
    {
      timeout: 15000,
    }
  );
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to list inventory items');
  }
  return data.data || [];
}

/**
 * Get inventory item by ID
 */
export async function getItem(company: string, itemId: string): Promise<InventoryItem> {
  const { data } = await apiClient.post(
    `${inventoryRpcBase(company)}/get-item`,
    { itemId },
    {
      timeout: 10000,
    }
  );
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to get inventory item');
  }
  return data.data;
}

/**
 * Get inventory item by SKU
 */
export async function getItemBySku(company: string, sku: string): Promise<InventoryItem> {
  const { data } = await apiClient.post(
    `${inventoryRpcBase(company)}/get-item-by-sku`,
    { sku },
    {
      timeout: 10000,
    }
  );
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to get inventory item by SKU');
  }
  return data.data;
}

/**
 * Create inventory item
 */
export async function createItem(
  company: string,
  payload: {
    sku: string;
    name: string;
    price: number;
    stock: number;
    tags?: string[];
  }
): Promise<InventoryItem> {
  const { data } = await apiClient.post(
    `${inventoryRpcBase(company)}/create-item`,
    payload,
    {
      timeout: 10000,
    }
  );
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to create inventory item');
  }
  return data.data;
}

/**
 * Update inventory item
 */
export async function updateItem(
  company: string,
  itemId: string,
  payload: {
    sku?: string;
    name?: string;
    price?: number;
    stock?: number;
    tags?: string[];
  }
): Promise<InventoryItem> {
  const { data } = await apiClient.post(
    `${inventoryRpcBase(company)}/update-item`,
    { itemId, ...payload },
    {
      timeout: 10000,
    }
  );
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to update inventory item');
  }
  return data.data;
}

/**
 * Update stock for an inventory item
 */
export async function updateStock(
  company: string,
  itemId: string,
  stock: number
): Promise<InventoryItem> {
  const { data } = await apiClient.post(
    `${inventoryRpcBase(company)}/update-stock`,
    { itemId, stock },
    {
      timeout: 10000,
    }
  );
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to update stock');
  }
  return data.data;
}

/**
 * Delete inventory item
 * IDEMPOTENT: Returns success even if item is already deleted
 */
export async function deleteItem(company: string, itemId: string): Promise<{ alreadyDeleted?: boolean }> {
  try {
    const { data } = await apiClient.post(
      `${inventoryRpcBase(company)}/delete-item`,
      { itemId },
      {
        timeout: 10000,
      }
    );
    
    // IDEMPOTENT DELETE: Accept success with alreadyDeleted flag
    if (data?.ok) {
      // Return with alreadyDeleted flag if present
      return {
        alreadyDeleted: data?.data?.alreadyDeleted || false
      };
    }
    
    throw new Error(data?.error || 'Failed to delete inventory item');
  } catch (err: any) {
    // Handle axios errors (404, 500, etc.)
    // Axios throws errors for non-2xx responses, so check response.data first
    if (err?.response?.data) {
      const responseData = err.response.data;
      
      // IDEMPOTENT DELETE: If gateway returns success with alreadyDeleted, treat as success
      if (responseData?.ok && responseData?.data?.alreadyDeleted) {
        return { alreadyDeleted: true };
      }
      
      if (responseData?.error) {
        throw new Error(responseData.error);
      }
      if (responseData?.message) {
        throw new Error(responseData.message);
      }
    }
    // If no response data, check the error message
    if (err?.message) {
      // If it's just "Request failed with status code 404", provide a better message
      if (err.message.includes('Request failed with status code 404')) {
        throw new Error('Item not found');
      }
      throw new Error(err.message);
    }
    // Fallback
    throw new Error(err?.response?.status === 404 ? 'Item not found' : 'Failed to delete inventory item');
  }
}

/**
 * Bulk update stock
 */
export async function bulkUpdateStock(
  company: string,
  updates: Array<{ sku: string; stock: number }>
): Promise<{ updated: number; items: InventoryItem[] }> {
  const { data } = await apiClient.post(
    `${inventoryRpcBase(company)}/bulk-update-stock`,
    { updates },
    {
      timeout: 15000,
    }
  );
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to bulk update stock');
  }
  return data.data;
}

